export const Transcript = {
  currentTime: 0,
  lastScrollTime: 0,
  playing: false,
  animationFrameRequest: null,
  currentWord: null,
  isProgrammaticScroll: false,
  lastActiveSegmentIndex: -1,

  mounted() {
    this.attachElements();

    this.video = document.querySelector("#video");

    this.updateActiveChapterIcon();

    this.attachDynamicListeners();

    this.video?.addEventListener("play", () => {
      this.playing = true;
      this.startLoop();
    });

    this.video?.addEventListener("timeupdate", () => {
      if (!this.playing) {
        setTimeout(() => {
          this.scrollAndHighlightClosestWord(this.video.currentTime);
          setTimeout(() => {
            this.highlightCurrentSentence(this.currentWord);
          }, 50);
        }, 50);
      }
    });

    this.video?.addEventListener("pause", () => {
      this.playing = false;
      this.stopLoop();
    });

    this.el
      .querySelector("#scroll-to-current-transcript")
      .addEventListener("scroll_to_current_moment", () => {
        // to disregard event parameter
        this.scrollToTranscript(this.currentWord.element.parentElement);
      });

    this.handleEvent("changed_language", this.changedLanguage.bind(this));

    this.handleEvent(
      "calculate_complete_transcript_scroll_position",
      this.calculateCompleteTranscriptScrollPosition.bind(this),
    );

    this.handleEvent(
      "show_complete_transcript_scroll_position",
      this.showCompleteTranscriptScrollPosition.bind(this),
    );

    this.handleEvent("scroll_to_current_transcript_moment", () => {
      this.scrollAndHighlightClosestWord(this.video.currentTime);
    });

    // go to segment directly
    const url = new URL(window.location.href);
    if (
      (url.searchParams.get("segment") || url.searchParams.get("start")) &&
      !this.playing
    ) {
      // use segment id
      if (url.searchParams.get("segment")) {
        const segment = this.el.querySelector(
          `#segment_${url.searchParams.get("segment")}`,
        );
        if (segment && segment.nextElementSibling) {
          this.scrollToTranscript(segment.nextElementSibling, "instant", 35);
        }
      }

      // use start time
      if (url.searchParams.get("start")) {
        const time = parseFloat(url.searchParams.get("start"));
        this.scrollAndHighlightClosestWord(time);
      }
    }

    const throttledScroll = this.throttle(this.scrolling.bind(this), 100);
    this.transcriptElement.addEventListener("scroll", throttledScroll);
  },

  updated() {
    this.attachElements();
    this.attachDynamicListeners();
    this.updateActiveChapterIcon();
  },

  attachDynamicListeners() {
    this.transcriptWordClickableElement();

    this.el
      .querySelectorAll(".segment-title-clickable-area")
      .forEach((segment) => {
        if (segment._clickListener) {
          segment.removeEventListener("click", segment._clickListener);
        }

        const listener = (e) => this.goToSegment(e);
        segment._clickListener = listener;
        segment.addEventListener("click", listener);
      });
  },

  calculateCompleteTranscriptScrollPosition({ position }) {
    this.scrollPosition = position;
    const loadCompleteElement = this.el.querySelector(
      `.load_complete_transcript_${this.scrollPosition}`,
    );

    this.oldElementTop =
      loadCompleteElement.getBoundingClientRect().top +
      (this.scrollPosition == "top" ? 72 : 0);

    this.pushEvent("open_complete_transcript", {});
  },

  showCompleteTranscriptScrollPosition() {
    const scrollToElement = this.el.querySelector(
      `.load_complete_transcript_${this.scrollPosition}`,
    );
    const newElementTop = scrollToElement.getBoundingClientRect().top;

    // Calculate the difference in position and adjust scroll
    const scrollAdjustment = newElementTop - this.oldElementTop;
    this.transcriptElement.scrollTop += scrollAdjustment;
  },

  transcriptWordClickableElement() {
    this.el
      .querySelectorAll('[data-element="transcript_text"]')
      .forEach((word) => {
        word.addEventListener("click", (e) => {
          const targetTime = parseFloat(e.target.dataset.time);

          if (!this.canSeekToTime(targetTime)) {
            this.openPreviewSubscriptionModal();
            return;
          }

          this.lastScrollTime = 0;
          this.isProgrammaticScroll = false;

          this.pushEvent("disable_playlist_bumper", {
            time: targetTime,
          });

          if (this.video) {
            this.video.currentTime = targetTime;

            if (this.video.paused) this.video.play();
          }
        });
      });

    if (!this._blockedClickListener) {
      this._blockedClickListener = (e) => {
        if (e.target.closest('[data-element="transcript_blocked"]')) {
          this.openPreviewSubscriptionModal();
        }
      };
      this.el.addEventListener("click", this._blockedClickListener);
    }
  },

  attachElements() {
    this.isPreview = this.el.dataset.preview === "true";
    const parsedPreviewStart = Number(this.el.dataset.previewStart);
    const parsedPreviewEnd = Number(this.el.dataset.previewEnd);
    this.previewStart = Number.isFinite(parsedPreviewStart)
      ? parsedPreviewStart
      : null;
    this.previewEnd = Number.isFinite(parsedPreviewEnd) ? parsedPreviewEnd : null;

    this.transcriptElement = this.el.querySelector("#transcript-content");
    this.scrollToCurrentTranscript = this.el.querySelector(
      "#scroll-to-current-transcript",
    );
    this.allTranscriptWords = [
      ...this.el.querySelectorAll(".transcript-word"),
    ].map((element) => {
      return {
        element,
        start: parseFloat(element.dataset.time),
        end: parseFloat(element.dataset.end),
      };
    });
    this.segments = this.el.querySelectorAll(".segment");
    this.lastActiveSegmentIndex = -1;
  },

  startLoop() {
    const loop = () => {
      if (this.playing) {
        this.highlightTranscript();
        this.animationFrameRequest = requestAnimationFrame(loop);
      }
    };
    loop();
  },

  stopLoop() {
    if (this.animationFrameRequest) {
      cancelAnimationFrame(this.animationFrameRequest);
      this.animationFrameRequest = null;
    }
  },

  changedLanguage() {
    this.attachElements();
    this.transcriptWordClickableElement();
    this.scrollAndHighlightClosestWord(this.video?.currentTime);
  },

  scrollAndHighlightClosestWord(time) {
    // Find currentWord thats closest to this.currentTime (but might not exactly be in that time range)
    this.currentWord =
      this.allTranscriptWords.reduce((closestWord, currentWord) => {
        const currentTime = time;
        const currentWordDiff = Math.min(
          Math.abs(currentWord.start - currentTime),
          Math.abs(currentWord.end - currentTime),
        );

        if (
          closestWord === null ||
          currentWordDiff < closestWord.diff ||
          (currentWordDiff === closestWord.diff &&
            currentTime >= currentWord.start &&
            currentTime <= currentWord.end)
        ) {
          return {
            word: currentWord,
            diff: currentWordDiff,
          };
        }

        return closestWord;
      }, null)?.word || null;

    if (this.currentWord) {
      this.scrollToTranscript(
        this.currentWord.element.parentElement,
        "instant",
      );
    }

    this.highlightTranscript();
  },

  goToSegment(e) {
    const segment = e.target.closest(".segment");
    const segmentStart = parseFloat(segment.dataset.time);

    if (!this.canSeekToTime(segmentStart)) {
      this.openPreviewSubscriptionModal();
      return;
    }

    this.pushEvent("disable_playlist_bumper", {});

    this.video.currentTime = segmentStart;
    if (this.video.paused) this.video.play();

    if (this.el.dataset.chaptersCollapsed === "true") return;

    const bottomMargin = 100;

    if (
      segment &&
      segment.nextElementSibling &&
      !this.isElementVisible(segment.nextElementSibling, bottomMargin)
    ) {
      this.scrollToTranscript(segment.nextElementSibling, "smooth", 35);
    }
  },

  highlightTranscript() {
    this.updateActiveChapterIcon();

    // find transcript word with currentTime
    const currentWord = this.allTranscriptWords.find(
      (word) =>
        word.start <= this.video.currentTime &&
        word.end >= this.video.currentTime,
    );

    if (!currentWord || currentWord === this.currentWord) {
      return; // No current word found or we already highlighted this word
    }

    this.currentWord = currentWord;

    // Highlight the current sentence
    this.highlightCurrentSentence(currentWord);

    // Show button to scroll to current word
    this.checkIfScrollButtonIsNeeded();

    // Auto-scroll to current word if its almost at the bottom of the page
    this.possiblyAutoScrollToCurrentWord();
  },

  updateActiveChapterIcon() {
    if (!this.segments || !this.video) return;

    const currentTime = this.video.currentTime;
    let activeIndex = -1;

    this.segments.forEach((segment, index) => {
      const start = parseFloat(segment.dataset.time);
      // End time is the start of the next segment, or Infinity if last
      const nextSegment = this.segments[index + 1];
      const end = nextSegment ? parseFloat(nextSegment.dataset.time) : Infinity;

      if (currentTime >= start && currentTime < end) {
        activeIndex = index;
      }
    });

    if (this.lastActiveSegmentIndex !== activeIndex) {
      this.segments.forEach((segment, index) => {
        const icon = segment.querySelector(".segment-play-icon");
        if (icon) {
          if (index === activeIndex) {
            icon.classList.remove("opacity-0");
            icon.classList.add("opacity-100");
          } else {
            icon.classList.remove("opacity-100");
            icon.classList.add("opacity-0");
          }
        }
      });
      this.lastActiveSegmentIndex = activeIndex;
    }
  },

  highlightCurrentSentence(currentWord) {
    let startWord = currentWord;

    // Traverse backward to find the start of the sentence.
    // In preview mode, redacted placeholders are non-clickable siblings, so stop safely
    // when the previous element is not part of allTranscriptWords.
    while (startWord?.element?.previousElementSibling) {
      const previousElement = startWord.element.previousElementSibling;

      if (this.isSentenceBoundary(previousElement)) {
        break;
      }

      const previousWord = this.allTranscriptWords.find(
        (word) => word.element === previousElement,
      );

      if (!previousWord) {
        break;
      }

      startWord = previousWord;
    }

    // Loop through words (once) to highlight them correctly
    for (let word of this.allTranscriptWords) {
      // Set opacity to 50% for all words before
      if (word.start <= currentWord.start) {
        if (word.element.classList.contains("opacity-50")) {
          word.element.classList.remove("opacity-50");
        }
      }

      // Remove bg-sun-400 from all words before this sentence
      if (word.start < startWord.start) {
        if (word.element.classList.contains("bg-sun-400")) {
          word.element.classList.remove("bg-sun-400", "dark:bg-sun-400/20");
        }
      }

      // Add bg-sun-400 in all words in this sentence and this current word
      if (word.start >= startWord.start && word.start <= currentWord.start) {
        if (!word.element.classList.contains("bg-sun-400")) {
          word.element.classList.add("bg-sun-400", "dark:bg-sun-400/20");
        }
      }

      // reset future classes
      if (word.start > currentWord.start) {
        if (!word.element.classList.contains("opacity-50")) {
          word.element.classList.add("opacity-50");
        }
        if (word.element.classList.contains("bg-sun-400")) {
          word.element.classList.remove("bg-sun-400", "dark:bg-sun-400/20");
        }
      }
    }
  },

  checkIfScrollButtonIsNeeded() {
    if (this.currentWord && !this.isProgrammaticScroll) {
      if (
        this.scrollToCurrentTranscript.classList.contains("bottom-0") &&
        this.isElementVisible(this.currentWord.element)
      ) {
        this.scrollToCurrentTranscript.classList.remove("bottom-0");
        this.scrollToCurrentTranscript.classList.add("-bottom-36");
      }

      if (
        this.scrollToCurrentTranscript.classList.contains("-bottom-36") &&
        !this.isElementVisible(this.currentWord.element)
      ) {
        this.scrollToCurrentTranscript.classList.remove("-bottom-36");
        this.scrollToCurrentTranscript.classList.add("bottom-0");
      }
    }
  },

  possiblyAutoScrollToCurrentWord() {
    if (this.lastScrollTime && Date.now() - this.lastScrollTime < 30000) return;

    const bottomMargin = 100;

    if (!this.isElementVisible(this.currentWord.element, bottomMargin)) {
      this.scrollToTranscript(this.currentWord.element.parentElement);
    }
  },

  isSentenceBoundary(element) {
    const text = element.textContent.trim();
    return text.endsWith(".") || text.endsWith("?") || text.endsWith("!");
  },

  isElementVisible(element, bottomMargin = 0) {
    const elementRect = element.getBoundingClientRect();
    const transcriptRect = this.transcriptElement.getBoundingClientRect();

    return (
      elementRect.top >= transcriptRect.top &&
      elementRect.bottom <= transcriptRect.bottom - bottomMargin
    );
  },

  scrollToTranscript(element, behavior = "smooth", margin = 50) {
    if (!element) return;
    const { top: containerTop } =
      this.transcriptElement.getBoundingClientRect();
    const { top: targetTop } = element.getBoundingClientRect();

    const top =
      targetTop - containerTop + this.transcriptElement.scrollTop - margin;

    if (top > 0) {
      this.transcriptElement.scrollTo({ top, behavior });
      this.isProgrammaticScroll = true;
      setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 1000);
    }
  },

  scrolling(e) {
    if (this.isProgrammaticScroll) return;
    this.checkIfScrollButtonIsNeeded();
    this.lastScrollTime = Date.now();
  },

  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },

  canSeekToTime(time) {
    if (!this.isPreview) return true;
    if (!Number.isFinite(time)) return false;
    if (!Number.isFinite(this.previewStart) || !Number.isFinite(this.previewEnd)) {
      return false;
    }

    return time >= this.previewStart - 0.001 && time < this.previewEnd;
  },

  openPreviewSubscriptionModal() {
    const trigger = document.getElementById("preview-subscription-trigger");
    if (trigger) trigger.click();
  },
};
